// src/layers/imagery/coverageAtPoint.js
//
// "Does this acquisition have PIXELS at this point?" — answered client-side,
// from the dissolved fine footprint the catalog now carries.
//
// ── WHY THIS IS NOT A SERVER CALL ──────────────────────────────────────────
//
// The obvious shape is an endpoint: give it a bbox, get back the dates with
// coverage. GET /imagery/footprints already accepts a bbox and would answer it
// in about 0.7 ms of database time. It is still the wrong shape, for two
// reasons that have nothing to do with that number.
//
// The strip has to recompute on every pan, and it is the ONLY explanation for a
// blank map. A network round trip means the strip lags the map, so during the
// lag it is showing the previous viewport's answer — which is precisely the
// interval in which a user pans into empty ground and looks at the strip to ask
// why. Debouncing makes that worse, not better.
//
// And it already has the data. Drawing itself requires knowing each date's
// coverage, so a second source for the same fact could only introduce a way for
// the two to disagree.
//
// MEASURED: 4,424 vertices across the three current collects, full check of all
// three, ~48 microseconds. That is 0.3% of one frame at 60fps. Both existing
// `map.on("move")` handlers in MapView are unthrottled and there is no debounce
// helper in the codebase to follow, so this runs per move like they do.
//
// ── WHY RAY CASTING AND NOT A LIBRARY ──────────────────────────────────────
//
// @turf/area and @turf/length are dependencies; @turf/boolean-point-in-polygon
// is not. This is ~25 lines against adding a package to answer one question, on
// geometry whose shape is already known: GeoJSON Polygon or MultiPolygon in
// EPSG:4326, from ST_AsGeoJSON. Add the dependency if a second caller needs
// more than this.
//
// ── NULL IS "UNKNOWN", AND IT IS NOT "NO" ──────────────────────────────────
//
// An acquisition whose geometry did not arrive returns null, never false. The
// two are different states and only one of them means "the map is blank here".
// Rendering a date as muted because the CATALOG failed would blame the imagery
// for a fetch error, and the user would pin it, see nothing, and be told by the
// strip that nothing is what to expect. Callers decide what to do with unknown;
// they are not allowed to conflate it with absence by accident.

/**
 * Is a point inside a linear ring? Standard even-odd ray cast.
 * @param {[number,number]} pt   [lon, lat]
 * @param {number[][]} ring      GeoJSON linear ring
 */
function pointInRing(pt, ring) {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    // Strict on one side, non-strict on the other, so a vertex is counted once.
    const straddles = (yi > y) !== (yj > y);
    if (straddles && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * Is a point inside a GeoJSON Polygon or MultiPolygon?
 *
 * Ring 0 of each polygon is the exterior; rings 1..n are holes. A point in a
 * hole is NOT covered — the current footprints have no holes (ST_UnaryUnion of
 * a tiled grid produces none), but a future collect with a gap in the middle
 * would, and silently treating a hole as solid would report coverage over the
 * one place that provably has none.
 *
 * @param {object|null} geometry  GeoJSON geometry, EPSG:4326
 * @param {[number,number]} pt    [lon, lat]
 * @returns {boolean|null}        null when geometry is absent or unusable
 */
export function geometryCoversPoint(geometry, pt) {
  if (!geometry || !Array.isArray(pt) || pt.length < 2) return null;
  if (!Number.isFinite(pt[0]) || !Number.isFinite(pt[1])) return null;

  const { type, coordinates } = geometry;
  if (!Array.isArray(coordinates)) return null;

  const polygons =
    type === "Polygon" ? [coordinates]
    : type === "MultiPolygon" ? coordinates
    : null;
  if (!polygons) return null;

  for (const rings of polygons) {
    if (!Array.isArray(rings) || !rings.length) continue;
    if (!pointInRing(pt, rings[0])) continue;
    let inHole = false;
    for (let k = 1; k < rings.length; k++) {
      if (pointInRing(pt, rings[k])) { inHole = true; break; }
    }
    if (!inHole) return true;
  }
  return false;
}

/**
 * Coverage state for every acquisition in a series, at one point.
 *
 * Returns a Map keyed by acquisition_date so a caller can look up the state of
 * a date it already holds, rather than searching a list. Values are
 * true / false / null with the meanings above.
 *
 * @param {object|null} series  a Series from groupFootprintsIntoSeries
 * @param {[number,number]} pt  [lon, lat]
 * @returns {Map<string, boolean|null>}
 */
export function coverageByDate(series, pt) {
  const out = new Map();
  for (const acq of series?.acquisitions ?? []) {
    out.set(acq.acquisition_date, geometryCoversPoint(acq.geometry, pt));
  }
  return out;
}

/**
 * THE NEWEST ACQUISITION WITH COVERAGE AT THIS POINT — which is what "latest"
 * has to mean, and is not the same as the newest acquisition.
 *
 * The two diverge the moment a partial collect is newer than a full one, and
 * that case is live: 2026_03_01 covers 721 km2 of ground that 2025_12_13 covers
 * 9,886 km2 of. Five kilometres apart, the answer is March on one side of a
 * line and December on the other. Taking `acquisitions.at(-1)` would return
 * March at both, and at the second point March renders nothing at all.
 *
 * UNKNOWN COVERAGE DOES NOT WIN. A date whose geometry is missing is skipped
 * rather than selected: choosing it would put the map's contents at the mercy
 * of a fetch that failed, and the failure mode is a blank map that the strip
 * says should have imagery. Skipping degrades to an older date that is KNOWN to
 * cover, which is wrong in a way the user can see and act on.
 *
 * @param {object|null} series  a Series, acquisitions sorted ascending by date
 * @param {[number,number]} pt  [lon, lat] — the viewport CENTRE, see below
 * @returns {object|null}       the acquisition, or null when none covers
 */
export function latestWithCoverage(series, pt) {
  const acquisitions = series?.acquisitions ?? [];
  for (let i = acquisitions.length - 1; i >= 0; i--) {
    if (geometryCoversPoint(acquisitions[i].geometry, pt) === true) {
      return acquisitions[i];
    }
  }
  return null;
}

// ── WHY THE CENTRE, AND NOT THE VIEWPORT ───────────────────────────────────
//
// "Latest" is a per-point question and the map is an area, so something has to
// collapse it. The candidates are the centre, "any pixel in view", and "the
// majority of the view".
//
// "Any pixel in view" is wrong because it selects a date that clips one corner
// of the screen. Such a date should be AVAILABLE in the strip — and it is,
// since the strip shows every entitled date — but it should not be what the map
// switches to. "Majority" is defensible and much more expensive, and it changes
// answer under pans that do not change what the user is looking at.
//
// The centre is what the user is looking at, it is stable under small pans, and
// it is one point rather than a polygon intersection. When latest differs
// across the visible area the map shows one date and the strip shows which —
// the muted states already communicate that other dates cover part of the view.
// Auto-switching mid-pan would be worse than a stable, explicable choice.
